import { useMatterConflictConfirmation } from "@/components/features/matter/layout/useMatterConflictConfirmation";
import type { createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import {
  DateFormField,
  Form,
  FormSubmitButton,
  SheetFooter,
  SimpleSelectFormField,
  TextAreaFormField,
  Typography,
} from "@/components/ui";
import { AddContactFormField } from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { CheckboxFormField } from "@/components/ui/FormFields/CheckboxFormField/CheckboxFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MatterContactConnectionModalPreview } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionModalPreview";
import { useUser } from "@/components/UserProvider";
import { useCreateMatterContactConnection } from "@/lib/data/react-query/matters/matter-contact-connection";
import { useCreateConnection } from "@/lib/data/react-query/connections/connection";
import { BaseContactSchema } from "@/lib/data/schemas";
import { MatterContactConnectionSchema } from "@/lib/data/schemas/matters/MatterSchema";
import {
  MATTER_CONTACT_CONNECTION_FAULT_STATUS_OPTIONS,
  NOT_APPLICABLE_INSURANCE_COVERAGE,
} from "@/lib/data/static/matters/insuranceCoverageStatics";
import {
  MATTER_CONTACT_CONNECTION_WITNESS_STATUS_OPTIONS,
  type MATTER_CONTACT_CONNECTION_LABEL_OPTIONS,
} from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type Contact,
  type MatterContactConnection,
  type MatterSubItemType,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatContactsName } from "@/lib/utils/format";
import {
  createMatterPartyConnectionInput,
  isSupportedMatterPartyRole,
} from "@/lib/utils/connections/matter-party-connections";
import {
  labelsMatch,
  type MatterContactLabelPair,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { modalFormFieldsetClassName } from "@/lib/utils/modal-classes";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

export type CreatingMatterContactConnection = {
  contact: Contact;
} & Pick<
  MatterContactConnection,
  | "contactId"
  | "dateTimeEnd"
  | "dateTimeStart"
  | "description"
  | "expenseId"
  | "faultStatus"
  | "insuranceCoverage"
  | "insuranceId"
  | "label"
  | "lienId"
  | "matterId"
  | "vehicleId"
  | "witnessStatus"
>;
export interface AddMatterContactConnectionFormProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  setIsDirty: (dirty: boolean) => void;
  labelOptions: (typeof MATTER_CONTACT_CONNECTION_LABEL_OPTIONS)[number][];
  disabledOptions?: (typeof MATTER_CONTACT_CONNECTION_LABEL_OPTIONS)[number][];
  subItem?: MatterSubItemType;
  subItemId?: string;
  matterId: string;
  disableFormSubmit?: boolean;
  disableTime?: boolean;
  overrideCreateFn?: (connection: CreatingMatterContactConnection) => void;
  defaultCreateContactValues?: Partial<z.infer<typeof createContactFormSchema>>;
  defaultContactSearchObject?: { [key: string]: string[] };
  excludeContactIds?: string[];
  /** Block creating the same contact + label again (e.g. other parties). */
  reservedContactLabelPairs?: MatterContactLabelPair[];
  autoOpenContactSelection?: boolean;
  /** Inline contact list + submit as soon as a row is chosen. */
  selectContactOnly?: boolean;
  /** Layout for the field grid + footer (modal vs slideover shell). */
  formShell?: "slideover" | "modal";
  /** Sync embedded create-contact UI (inline picker) with parent `AppModal`. */
  onInlineEmbeddedCreateStateChange?: (state: {
    active: boolean;
    dirty: boolean;
    loading: boolean;
  }) => void;
  inlineEmbeddedCreateOpen?: boolean;
  onInlineEmbeddedCreateOpenChange?: (open: boolean) => void;
  /** When true, flex + overflow chain is sized for embedded create in a `bodyContained` modal. */
  inlineEmbeddedCreateActive?: boolean;
}

const formSchema = z
  .object({
    contact: BaseContactSchema.pick({
      id: true,
    }).passthrough(),
  })
  .merge(
    MatterContactConnectionSchema.pick({
      description: true,
      label: true,
      dateTimeStart: true,
      dateTimeEnd: true,
      faultStatus: true,
      witnessStatus: true,
    }),
  )
  .extend({
    insuranceNotApplicable: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.dateTimeEnd && !data.dateTimeStart) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dateTimeStart"],
        message: "Cannot have only an end date",
        fatal: true,
      });
      return;
    }
    if (
      data.dateTimeEnd &&
      dayjs(data.dateTimeEnd).isBefore(data.dateTimeStart)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dateTimeEnd"],
        message: "End must be after start",
        fatal: true,
      });
      return;
    }
    if (!data.contact.id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["contact"],
        message: "Contact is required",
        fatal: true,
      });
    }
    return true;
  });

const defaultFormValues = {
  contact: {
    id: "",
  },
  description: "",
  dateTimeStart: dayjs().format("YYYY-MM-DD").toString(),
  dateTimeEnd: "",
  label: "",
  faultStatus: undefined,
  witnessStatus: undefined,
  insuranceNotApplicable: false,
} as const;

function buildAddMatterContactConnectionFormValues(): z.infer<
  typeof formSchema
> {
  return {
    ...defaultFormValues,
    dateTimeStart: dayjs().format("YYYY-MM-DD"),
  };
}

const KNOWN_FIXED_LABELS = new Set<
  (typeof MATTER_CONTACT_CONNECTION_LABEL_OPTIONS)[number]
>([
  "Client",
  "Defendant",
  "Primary Contact",
  "Spouse",
  "Parent",
  "Emergency Contact",
]);

export const AddMatterContactConnectionForm = ({
  disableFormSubmit,
  disableTime,
  isOpen,
  labelOptions,
  disabledOptions,
  matterId,
  setIsDirty,
  setIsOpen,
  subItem,
  subItemId,
  overrideCreateFn,
  defaultCreateContactValues,
  defaultContactSearchObject,
  excludeContactIds,
  reservedContactLabelPairs,
  autoOpenContactSelection,
  selectContactOnly,
  formShell = "slideover",
  onInlineEmbeddedCreateStateChange,
  inlineEmbeddedCreateOpen,
  onInlineEmbeddedCreateOpenChange,
}: AddMatterContactConnectionFormProps) => {
  const fieldsetClassName =
    formShell === "modal"
      ? modalFormFieldsetClassName
      : slideoverFieldsetClassName;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: buildAddMatterContactConnectionFormValues(),
    mode: "onSubmit",
  });
  /** Captured before closing the shell so `form.reset()` on close does not lose label/dates for submit. */
  const selectOnlyFormSnapshotRef = useRef<z.infer<typeof formSchema> | null>(
    null,
  );
  const connectionCreateIntentRef = useRef<{
    key: string;
    operationId: string;
  } | null>(null);
  const { hasActionPermission } = useUser();
  const { confirmParty, conflictModal, isCheckingConflict } =
    useMatterConflictConfirmation(isOpen);

  const [selectedContact, setSelectedContact] = useState<Contact | undefined>(
    undefined,
  );
  const createMatterContactConnection = useCreateMatterContactConnection({
    showToast: false,
  });
  const createConnection = useCreateConnection();
  const selectedLabel = form.watch("label");
  const singleLabelOption =
    labelOptions.length === 1 ? labelOptions[0] : undefined;
  const resolvedLabel = singleLabelOption ?? selectedLabel.trim();
  const watchedContact = form.watch("contact") as unknown as
    | Contact
    | undefined;
  const selectedContactId = selectedContact?.id || watchedContact?.id || "";
  const isReservedContactLabelPair = (
    contactId: string,
    label: string,
  ): boolean =>
    Boolean(
      contactId &&
        label &&
        reservedContactLabelPairs?.some(
          (pair) =>
            pair.contactId === contactId && labelsMatch(pair.label, label),
        ),
    );
  const disabledLabelOptionsForContact =
    useMemo((): (typeof MATTER_CONTACT_CONNECTION_LABEL_OPTIONS)[number][] => {
      if (!selectedContactId || !reservedContactLabelPairs?.length) {
        return disabledOptions ?? [];
      }
      const takenLabels = reservedContactLabelPairs
        .filter((pair) => pair.contactId === selectedContactId)
        .map((pair) => pair.label);
      return [
        ...new Set([...(disabledOptions ?? []), ...takenLabels]),
      ] as (typeof MATTER_CONTACT_CONNECTION_LABEL_OPTIONS)[number][];
    }, [disabledOptions, reservedContactLabelPairs, selectedContactId]);
  const isDuplicateReservedPair = isReservedContactLabelPair(
    selectedContactId,
    resolvedLabel,
  );
  const canSubmitCreate = Boolean(
    selectedContactId && resolvedLabel && !isDuplicateReservedPair,
  );
  const shouldHideLabelSelect = Boolean(
    singleLabelOption && KNOWN_FIXED_LABELS.has(singleLabelOption),
  );
  const showInsuranceToggle = selectedLabel === "Client";
  const showFaultStatus = selectedLabel === "Defendant";
  const showWitnessStatus = selectedLabel === "Witness";
  const inlineContactPickerDefaultSearch = useMemo(() => {
    if (!selectContactOnly) {
      return undefined;
    }
    if (defaultContactSearchObject) {
      return defaultContactSearchObject;
    }
    if (!defaultCreateContactValues) {
      return undefined;
    }
    const individual =
      defaultCreateContactValues.individualSubTypes?.filter(Boolean);
    const org =
      defaultCreateContactValues.organizationSubTypes?.filter(Boolean);
    if (individual?.length) {
      return { individualSubTypes: individual } as {
        [key: string]: string[];
      };
    }
    if (org?.length) {
      return { organizationSubTypes: org } as {
        [key: string]: string[];
      };
    }
    return undefined;
  }, [
    selectContactOnly,
    defaultContactSearchObject,
    defaultCreateContactValues,
  ]);
  useEffect(() => {
    if (!showInsuranceToggle) {
      form.setValue("insuranceNotApplicable", false);
    }
  }, [showInsuranceToggle, form]);
  useEffect(() => {
    if (!showFaultStatus) {
      form.setValue("faultStatus", undefined);
    }
  }, [showFaultStatus, form]);
  useEffect(() => {
    if (!showWitnessStatus) {
      form.setValue("witnessStatus", undefined);
    }
  }, [showWitnessStatus, form]);

  const submitConnection = async (
    contact: Contact,
    data: z.infer<typeof formSchema>,
    options?: { skipSlideoverClose?: boolean },
  ) => {
    if (subItem === "insurance") {
      toast.error(
        "Insurance contacts must be managed from the insurance form.",
      );
      return;
    }
    if (excludeContactIds?.includes(contact.id)) {
      toast.error("This contact has already been added.");
      return;
    }

    const resolvedLabel = singleLabelOption ?? data.label;
    if (isReservedContactLabelPair(contact.id, resolvedLabel)) {
      toast.error(
        `${formatContactsName(contact) ?? "This contact"} is already a ${resolvedLabel} on this matter.`,
      );
      return;
    }

    if (
      !subItem &&
      !(await confirmParty({
        contactId: contact.id,
        label: resolvedLabel,
        matterId: overrideCreateFn ? undefined : matterId,
      }))
    ) {
      return;
    }

    const payload = {
      vehicleId: subItem === "vehicle" ? (subItemId ?? null) : null,
      insuranceId: null,
      expenseId: subItem === "expense" ? (subItemId ?? null) : null,
      lienId: subItem === "lien" ? (subItemId ?? null) : null,
      contactId: contact.id,
      contact,
      label: resolvedLabel,
      matterId: matterId,
      description: data.description,
      dateTimeStart: data.dateTimeStart ?? "",
      dateTimeEnd: data.dateTimeEnd ?? "",
      insuranceCoverage: data.insuranceNotApplicable
        ? NOT_APPLICABLE_INSURANCE_COVERAGE
        : null,
      faultStatus: data.faultStatus,
      witnessStatus: data.witnessStatus,
    };
    if (overrideCreateFn) {
      overrideCreateFn(payload);
      if (!options?.skipSlideoverClose) {
        setIsOpen(false);
      }
      return;
    }

    try {
      if (!subItem) {
        if (!isSupportedMatterPartyRole(resolvedLabel)) {
          toast.error(`${resolvedLabel} is not supported as a matter party.`);
          return;
        }
        const selectedStartDate = data.dateTimeStart
          ? dayjs(data.dateTimeStart)
          : null;
        const connectionInput = createMatterPartyConnectionInput({
          contactId: contact.id,
          dateTimeEnd: data.dateTimeEnd
            ? dayjs(data.dateTimeEnd).startOf("day").toISOString()
            : null,
          // The form defaults to today's date. Omitting that timestamp lets
          // the backend start the assignment at the exact creation time,
          // avoiding overlap with a same-day assignment that was just ended.
          // Explicit past or future dates retain their day boundary.
          dateTimeStart:
            selectedStartDate && !selectedStartDate.isSame(dayjs(), "day")
              ? selectedStartDate.startOf("day").toISOString()
              : undefined,
          description: data.description ?? null,
          faultStatus: data.faultStatus ?? null,
          insuranceCoverage: data.insuranceNotApplicable
            ? NOT_APPLICABLE_INSURANCE_COVERAGE
            : null,
          matterId,
          role: resolvedLabel,
          witnessStatus: data.witnessStatus ?? null,
        });
        const connectionIntentKey = JSON.stringify({
          ...connectionInput,
          operationId: undefined,
        });
        if (connectionCreateIntentRef.current?.key !== connectionIntentKey) {
          connectionCreateIntentRef.current = {
            key: connectionIntentKey,
            operationId: crypto.randomUUID(),
          };
        }
        await createConnection.mutateAsync({
          ...connectionInput,
          operationId: connectionCreateIntentRef.current.operationId,
          matterId,
        });
      } else {
        await createMatterContactConnection.mutateAsync(payload);
      }
    } catch {
      if (subItem) {
        toast.error("Error creating matter contact connection");
      }
      return;
    }

    const successMessage =
      resolvedLabel === "Defendant"
        ? "Defendant created successfully"
        : resolvedLabel === "Client"
          ? "Client added to matter successfully"
          : `${resolvedLabel} connection created successfully`;

    toast.success(successMessage);
    if (!options?.skipSlideoverClose) {
      setIsOpen(false);
    }
  };

  const onSubmitForm = async (data: z.infer<typeof formSchema>) => {
    const formContact = data.contact as unknown as Contact | undefined;
    const contact = selectedContact?.id
      ? selectedContact
      : formContact?.id
        ? formContact
        : undefined;
    if (!contact?.id) {
      toast.error("Please select a contact.");
      return;
    }
    await submitConnection(contact, data);
  };

  const submitFromPickedContact = async (contact: Contact) => {
    const data = selectOnlyFormSnapshotRef.current ?? form.getValues();
    selectOnlyFormSnapshotRef.current = null;
    await submitConnection(contact, data, {
      skipSlideoverClose: data.label !== "Client" && data.label !== "Defendant",
    });
  };

  const onInlineContactPickInitiated = selectContactOnly
    ? () => {
        selectOnlyFormSnapshotRef.current = form.getValues();
        if (resolvedLabel !== "Client" && resolvedLabel !== "Defendant") {
          setIsOpen(false);
        }
      }
    : undefined;

  const prevIsOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      form.reset(buildAddMatterContactConnectionFormValues());
      setSelectedContact(undefined);
      if (labelOptions.length === 1) {
        form.setValue("label", labelOptions[0], {
          shouldDirty: false,
        });
      }
    }
    if (!isOpen && prevIsOpenRef.current) {
      form.reset(buildAddMatterContactConnectionFormValues());
      setSelectedContact(undefined);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, form, labelOptions]);

  useEffect(() => {
    setIsDirty(form.formState.isDirty);
  }, [form.formState.isDirty, setIsDirty]);

  const previewConnectionDraft = useMemo(():
    | MatterContactConnection
    | undefined => {
    const label = selectedLabel.trim();
    if (!label) {
      return undefined;
    }
    return {
      label: label,
      ...(selectedContact?.id ? { contactId: selectedContact.id } : {}),
    } as MatterContactConnection;
  }, [selectedContact?.id, selectedLabel]);

  const formFooter = !disableFormSubmit && (
    <SheetFooter addSlideoverStyles>
      <div className="flex justify-end space-x-2">
        <FormCancelButton
          setOpen={setIsOpen}
          isDirty={form.formState.isDirty}
        />
        <FormSubmitButton
          overrideDisabled
          disabled={!canSubmitCreate}
          loading={
            isCheckingConflict ||
            createMatterContactConnection.isPending ||
            createConnection.isPending
          }
        />
      </div>
    </SheetFooter>
  );

  return (
    <Form {...form}>
      {conflictModal}
      {selectContactOnly ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <fieldset
            className={cn(
              fieldsetClassName,
              "min-h-0 flex-1 [grid-template-rows:minmax(0,1fr)] overflow-hidden",
            )}
            disabled={
              !hasActionPermission(
                ACTION_PERMISSION_KEYS.matter,
                ACTION_PERMISSION_VALUES.update,
              )
            }
          >
            <div
              className={cn(
                "col-span-full flex min-h-0 min-w-0 flex-col overflow-hidden",
              )}
            >
              <AddContactFormField
                register={form.register("contact")}
                setSelectedContact={setSelectedContact}
                matterId={matterId}
                defaultValues={defaultCreateContactValues}
                excludeContactIds={excludeContactIds}
                defaultSearchObject={inlineContactPickerDefaultSearch}
                autoOpenContactSlideover={autoOpenContactSelection}
                parentSlideoverOpen={isOpen}
                inlineContactPicker
                onContactSelectedForSubmit={submitFromPickedContact}
                onInlineContactPickInitiated={onInlineContactPickInitiated}
                createNewContactLabel="Create new contact"
                onInlineEmbeddedCreateStateChange={
                  onInlineEmbeddedCreateStateChange
                }
                inlineEmbeddedCreateOpen={inlineEmbeddedCreateOpen}
                onInlineEmbeddedCreateOpenChange={
                  onInlineEmbeddedCreateOpenChange
                }
                hideInlineCreateContactAction
              />
            </div>
          </fieldset>
        </div>
      ) : (
        <form
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit(onSubmitForm)();
          }}
        >
          {formShell === "modal" ? (
            <div className="flex min-h-0 flex-1 flex-col lg:flex-row lg:items-stretch">
              <div
                className={cn(
                  "border-border bg-muted/15 flex max-h-[min(40vh,320px)] min-h-0 shrink-0 flex-col overflow-hidden border-b",
                  "lg:max-h-none lg:w-[min(100%,380px)] lg:max-w-[380px] lg:self-stretch lg:border-r lg:border-b-0",
                )}
              >
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                  <div className="px-5 pt-3 pb-4">
                    <AddContactFormField
                      contactSelectorSize="compact"
                      hideCompactContactChrome
                      label="Contact*"
                      register={form.register("contact")}
                      setSelectedContact={setSelectedContact}
                      matterId={matterId}
                      defaultValues={defaultCreateContactValues}
                      excludeContactIds={excludeContactIds}
                      autoOpenContactSlideover={autoOpenContactSelection}
                      parentSlideoverOpen={isOpen}
                      createNewContactLabel="Create new contact"
                      renderSelectedContactPreview={(contact) => (
                        <MatterContactConnectionModalPreview
                          contactId={contact.id}
                          previewContact={contact}
                          matterId={matterId}
                          matterContactConnection={previewConnectionDraft}
                          onRemoveContact={() => {
                            setSelectedContact(undefined);
                            form.setValue(
                              "contact",
                              { id: "" },
                              { shouldDirty: true },
                            );
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pt-3 pb-4">
                  <fieldset
                    className={modalFormFieldsetClassName}
                    disabled={
                      !hasActionPermission(
                        ACTION_PERMISSION_KEYS.matter,
                        ACTION_PERMISSION_VALUES.update,
                      )
                    }
                  >
                    {!shouldHideLabelSelect && (
                      <SimpleSelectFormField
                        register={form.register("label")}
                        items={labelOptions}
                        placeholder="Select a label"
                        label="Connection label*"
                        disabledItems={disabledLabelOptionsForContact}
                      />
                    )}
                    <div className="col-span-2">
                      <TextAreaFormField
                        label="Description"
                        register={form.register("description")}
                      />
                    </div>
                    {!disableTime && (
                      <>
                        <DateFormField
                          label="Start date"
                          register={form.register("dateTimeStart")}
                          name="dateTimeStart"
                        />
                        <DateFormField
                          label="End date"
                          register={form.register("dateTimeEnd")}
                          name="dateTimeEnd"
                        />
                      </>
                    )}
                    {showInsuranceToggle && (
                      <CheckboxFormField
                        register={form.register("insuranceNotApplicable")}
                        label={
                          <Typography size="sm" weight="medium">
                            No insurance
                          </Typography>
                        }
                      />
                    )}
                    {showFaultStatus && (
                      <SimpleSelectFormField
                        register={form.register("faultStatus")}
                        items={[
                          ...MATTER_CONTACT_CONNECTION_FAULT_STATUS_OPTIONS,
                        ]}
                        placeholder="Select fault status"
                        label="Fault status"
                      />
                    )}
                    {showWitnessStatus && (
                      <SimpleSelectFormField
                        register={form.register("witnessStatus")}
                        items={[
                          ...MATTER_CONTACT_CONNECTION_WITNESS_STATUS_OPTIONS,
                        ]}
                        placeholder="Select witness status"
                        label="Witness status"
                      />
                    )}
                  </fieldset>
                </div>
                {formFooter}
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-y-auto">
                <fieldset
                  className={fieldsetClassName}
                  disabled={
                    !hasActionPermission(
                      ACTION_PERMISSION_KEYS.matter,
                      ACTION_PERMISSION_VALUES.update,
                    )
                  }
                >
                  <AddContactFormField
                    label="Contact*"
                    register={form.register("contact")}
                    setSelectedContact={setSelectedContact}
                    matterId={matterId}
                    defaultValues={defaultCreateContactValues}
                    excludeContactIds={excludeContactIds}
                    autoOpenContactSlideover={autoOpenContactSelection}
                    parentSlideoverOpen={isOpen}
                    createNewContactLabel="Create new contact"
                  />
                  {!shouldHideLabelSelect && (
                    <SimpleSelectFormField
                      register={form.register("label")}
                      items={labelOptions}
                      placeholder="Select a label"
                      label="Connection label*"
                      disabledItems={disabledLabelOptionsForContact}
                    />
                  )}
                  <div className="col-span-2">
                    <TextAreaFormField
                      label="Description"
                      register={form.register("description")}
                    />
                  </div>
                  {!disableTime && (
                    <>
                      <DateFormField
                        label="Start date"
                        register={form.register("dateTimeStart")}
                        name="dateTimeStart"
                      />
                      <DateFormField
                        label="End date"
                        register={form.register("dateTimeEnd")}
                        name="dateTimeEnd"
                      />
                    </>
                  )}
                  {showInsuranceToggle && (
                    <CheckboxFormField
                      register={form.register("insuranceNotApplicable")}
                      label={
                        <Typography size="sm" weight="medium">
                          No insurance
                        </Typography>
                      }
                    />
                  )}
                  {showFaultStatus && (
                    <SimpleSelectFormField
                      register={form.register("faultStatus")}
                      items={[
                        ...MATTER_CONTACT_CONNECTION_FAULT_STATUS_OPTIONS,
                      ]}
                      placeholder="Select fault status"
                      label="Fault status"
                    />
                  )}
                  {showWitnessStatus && (
                    <SimpleSelectFormField
                      register={form.register("witnessStatus")}
                      items={[
                        ...MATTER_CONTACT_CONNECTION_WITNESS_STATUS_OPTIONS,
                      ]}
                      placeholder="Select witness status"
                      label="Witness status"
                    />
                  )}
                </fieldset>
              </div>
              <div className="flex-grow" />
              {formFooter}
            </>
          )}
        </form>
      )}
    </Form>
  );
};
